<template>
	<div class="container my-5">
		<div class="columns is-multiline is-justify-content-center">
			<template v-for="v in versions" :key="v">
				<div class="column is-1 game-flag" @click="filterByVersion(v)" :class="selectedVersion === v ? 'active' : ''">
					<div class="game-name">{{ v }}</div>
				</div>
			</template>
		</div>
	</div>
	<br />
	<div class="container mt-5">
		<div class="columns is-multiline">
			<add-card v-if="auth.state.isLoggedIn" column-class="column is-2" @click="openAdd"></add-card>
			<template v-for="g in itemsPaginated" :key="g.id">
				<div class="column is-2 is-flex is-flex-direction-column is-relative">
					<div class="is-relative image-wrap">
						<Carousel :items-to-show="itemsToShow" :mouse-drag="false">
							<Slide v-for="image in g.image.src" :key="image">
								<figure class="image is-3by4" @click="openGameModal(g.image)">
									<img :src="image" :alt="g.image.alt" class="is-relative" />
								</figure>
							</Slide>

							<template #addons>
								<Navigation />
							</template>
						</Carousel>
						<owned-or-not :owned="g.own"></owned-or-not>
						<div class="admin-actions" v-if="auth.state.isLoggedIn">
							<button class="icon-button" title="Edit" @click.stop="openEdit(g)">
								<i class="fas fa-pen"></i>
							</button>
							<button class="icon-button icon-button-delete" title="Delete" @click.stop="onDelete(g)">
								<i class="fas fa-trash"></i>
							</button>
						</div>
					</div>
					<ul v-if="g.version !== 'Demos'" class="item-info has-text-white is-size-7 p-2">
						<li><strong>Version:</strong> {{ g.version }}</li>
						<li><strong>Country:</strong> {{ g.country || 'Unknown' }}</li>
						<li><strong>EAN:</strong> {{ g.ean || '-' }}</li>
					</ul>
				</div>
			</template>
		</div>
		<empty-hero v-if="itemsPaginated.length === 0"></empty-hero>

		<game-modal :game="gameModalContent" v-if="showGameModal" @close-modal="showGameModal = false"></game-modal>

		<Transition name="fade">
			<back-to-top v-if="currentPage > 1"></back-to-top>
		</Transition>
	</div>
	<add-virtual-skipper-modal
		:show="showAddModal"
		:editing-item="editingItem"
		:versions="versions"
		:default-version="selectedVersion"
		:on-submit="handleSubmit"
		@close="onModalClose"
	></add-virtual-skipper-modal>
</template>

<script>
import GameModal from '@/components/GameModal.vue';
import EmptyHero from '@/components/EmptyHero.vue';
import OwnedOrNot from '@/components/OwnedOrNot.vue';
import BackToTop from '@/components/BackToTop.vue';
import AddCard from '@/components/AddCard.vue';
import AddVirtualSkipperModal from '@/components/AddVirtualSkipperModal.vue';
import auth from '@/auth.js';
import customItems from '@/customItems.js';
import { Carousel, Slide, Navigation } from 'vue3-carousel';
export default {
	name: 'VirtualSkipperView',
	props: ['ownFilter'],
	components: { Carousel, Slide, Navigation, GameModal, EmptyHero, OwnedOrNot, BackToTop, AddCard, AddVirtualSkipperModal },
	data() {
		return {
			auth,
			versions: ['Vsk1', 'Vsk2', 'Vsk3', 'Vsk4', 'Vsk5', 'Demos'],
			showGameModal: false,
			gameModalContent: null,
			itemsToShow: 1,
			currentPage: 1,
			perPage: 18,
			showAddModal: false,
			editingItem: null,
		};
	},
	computed: {
		selectedVersion() {
			return this.$route.params.version || '';
		},
		items() {
			return customItems.getItems('virtualskipper');
		},
		filteredItems() {
			return this.selectedVersion ? this.items.filter((it) => it.version === this.selectedVersion) : this.items;
		},
		itemsPaginated() {
			let ownFiltered = this.ownFilter !== undefined ? this.filteredItems.filter((g) => g.own === this.ownFilter) : this.filteredItems;
			return ownFiltered.slice(0, this.currentPage * this.perPage);
		},
	},
	methods: {
		filterByVersion(v) {
			const c = this.selectedVersion === v ? '' : v;
			this.$router.push({ name: 'virtualskipper', params: { version: c || undefined } });
		},
		openGameModal(g) {
			this.gameModalContent = g;
			this.showGameModal = true;
		},
		handleScroll() {
			if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 100) {
				this.currentPage++;
			} else if (window.scrollY === 0) {
				this.currentPage = window.innerHeight > 1080 ? 2 : 1;
			}
		},
		openAdd() {
			this.editingItem = null;
			this.showAddModal = true;
		},
		openEdit(item) {
			this.editingItem = item;
			this.showAddModal = true;
		},
		onModalClose() {
			this.showAddModal = false;
			this.editingItem = null;
		},
		async handleSubmit({ isEdit, id, payload }) {
			if (isEdit) {
				return customItems.updateItem('virtualskipper', id, payload);
			}
			return customItems.addItem('virtualskipper', payload);
		},
		async onDelete(item) {
			if (!confirm('Delete this item?')) return;
			const result = await customItems.deleteItem('virtualskipper', item.id);
			if (!result.ok) {
				alert(result.error || 'Could not delete this item.');
			}
		},
	},
	created() {
		this.currentPage = window.innerHeight > 1080 ? 2 : 1;
		window.addEventListener('scroll', this.handleScroll);
	},
	unmounted() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.column {
	background-color: #57462e;
	background-clip: content-box;
}
.image img {
	object-fit: cover !important;
	object-position: center !important;
}
.image-wrap :deep(.icon) {
	bottom: 0;
	right: 0;
}
.item-info {
	list-style: none;
	word-break: break-all;
}
.game-flag {
	background-color: rgb(7, 7, 7);
	height: 75px;
	width: 150px;
	border-radius: 15px;
	position: relative;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
}
.game-flag:hover .game-name,
.game-flag.active .game-name {
	color: rgb(72, 199, 142);
	font-weight: bold;
}
.admin-actions {
	position: absolute;
	top: 6px;
	right: 6px;
	z-index: 20;
	display: flex;
	gap: 6px;
}
.icon-button {
	width: 26px;
	height: 26px;
	padding: 0;
	margin: 0;
	border: none;
	border-radius: 50%;
	background-color: rgba(0, 0, 0, 0.6);
	color: white;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.7rem;
}
.icon-button:hover {
	background-color: #48c78e;
	color: #131417;
}
.icon-button-delete:hover {
	background-color: #f14668;
	color: white;
}
</style>
